import { Overlay, OverlayRef } from '@angular/cdk/overlay'
import { ComponentPortal } from '@angular/cdk/portal'

import {
  Component,
  EventEmitter,
  Input,
  Output,
  Injector,
  InjectionToken,
  ViewContainerRef,
  ChangeDetectionStrategy,
} from '@angular/core'
import { MatIcon } from '@angular/material/icon'

import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'
import { isEqual } from 'lodash'

import {
  MultipleSelectControlComponent,
  InputData,
} from './multiple-select-control/multiple-select-control.component'
import {
  FormControl,
  NG_VALUE_ACCESSOR,
  ControlValueAccessor,
} from '@angular/forms'

export type Item = {
  label: string
  id?: string
  checked: boolean
  notSelectable?: boolean
}

export const MULTIPLE_SELECT_INPUT = new InjectionToken('MULTIPLE_SELECT')
@UntilDestroy()
@Component({
  selector: 'app-multiple-select',
  standalone: true,
  imports: [MatIcon],
  templateUrl: './multiple-select.component.html',
  styleUrls: ['./multiple-select.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: MultipleSelectComponent,
      multi: true,
    },
  ],
})
export class MultipleSelectComponent implements ControlValueAccessor {
  @Input() formControl: FormControl | null = null
  @Input() fieldName = ''
  // トリガーのアクセシブル名 (フィールド名)。fieldName と違い可視ラベルを出さずに
  // スクリーンリーダー向けの名前だけ与える。未指定 (null) なら属性を付けず、
  // ボタン内容 (選択ラベル) がそのまま読まれる従来挙動。
  @Input() ariaLabel: string | null = null
  @Input() items: Item[] = []
  /** 一致条件の選択肢。指定する場合は matchMode も必須（null だと select が空欄表示になる）。 */
  @Input() matchOptions: readonly { value: string; label: string }[] | null =
    null
  /** 現在の一致条件。matchOptions を指定する場合は必須（null だと select が空欄表示になる）。 */
  @Input() matchMode: string | null = null
  @Output() readonly changeMatchMode = new EventEmitter<string>()
  @Input() showError = false
  /**
   * matchOptions を使う場合は、条件だけが変わったときも現在の選択を通知する。
   * 利用側は changeMatchMode で条件を受け取り、changeSelected で再検索する想定。
   */
  @Output() readonly changeSelected = new EventEmitter<Item[]>()

  controlOverlay: OverlayRef | null = null

  // aria-label はボタン内容のアクセシブル名を上書きするため、フィールド名だけを
  // 設定すると選択状態が読まれなくなる。現在の選択ラベルまで結合して返す。
  get triggerAriaLabel(): string | null {
    if (this.ariaLabel == null) {
      return null
    }
    return `${this.ariaLabel}: ${this.checkedItemsLabel}`
  }

  get checkedItems(): Item[] {
    return this.items.filter((item) => item.checked)
  }

  get allUnChecked(): boolean {
    return this.checkedItems.length === 0
  }

  get allChecked(): boolean {
    return this.checkedItems.length === this.items.length
  }

  get checkedItemsLabel(): string {
    if (this.allUnChecked) return 'なし'
    if (this.allChecked) return 'すべて選択'
    return this.checkedItems.map((item) => item.label).join(', ')
  }

  // NG01203 Missing value accessorの対応
  // 現在、NgModelの利用を想定していないため、警告を抑制するためにダミーの実装を行っている
  writeValue(): void {}
  registerOnChange(): void {}
  registerOnTouched(): void {}
  setDisabledState(): void {}

  constructor(
    private overlay: Overlay,
    private injector: Injector,
    private viewContainerRef: ViewContainerRef,
  ) {}

  showSelectControl(connectToElem: Element): void {
    this.formControl?.markAllAsTouched()
    if (this.controlOverlay) {
      this.detachControlOverlayOverlay()
    }

    this.controlOverlay = this.overlay.create({
      hasBackdrop: true,
      scrollStrategy: this.overlay.scrollStrategies.close(),
      positionStrategy: this.overlay
        .position()
        .flexibleConnectedTo(connectToElem)
        .withPositions([
          {
            originX: 'start',
            originY: 'bottom',
            overlayX: 'start',
            overlayY: 'top',
            offsetY: 2,
          },
        ]),
    })

    const data: InputData = {
      items: this.items,
      fieldName: this.fieldName,
      matchOptions: this.matchOptions,
      matchMode: this.matchMode,
    }
    const injector = Injector.create({
      parent: this.injector,
      providers: [
        {
          provide: MULTIPLE_SELECT_INPUT,
          useValue: data,
        },
      ],
    })

    const portal = new ComponentPortal(
      MultipleSelectControlComponent,
      this.viewContainerRef,
      injector,
    )
    const ref = this.controlOverlay.attach(portal)

    let lastMatchMode = this.matchMode
    ref.instance.changeMatchMode
      .pipe(untilDestroyed(this))
      .subscribe((mode) => {
        lastMatchMode = mode
      })
    let lastSelected: Item[] = this.items
    ref.instance.changeSelected
      .pipe(untilDestroyed(this))
      .subscribe((items) => {
        lastSelected = items
      })
    const closeCallback = () => {
      const matchMode = lastMatchMode
      const matchModeChanged = matchMode != null && this.matchMode !== matchMode
      if (matchModeChanged) {
        this.changeMatchMode.emit(matchMode)
      }
      if (matchModeChanged || !isEqual(this.items, lastSelected)) {
        this.changeSelected.emit(lastSelected)
      }

      this.detachControlOverlayOverlay()
    }
    ref.instance.closed.pipe(untilDestroyed(this)).subscribe(closeCallback)
    this.controlOverlay.backdropClick().subscribe(closeCallback)
  }

  private detachControlOverlayOverlay = (): void => {
    if (this.controlOverlay) {
      this.controlOverlay.detach()
      this.controlOverlay.dispose()
      this.controlOverlay = null
    }
  }
}
